import React, { useEffect, useState } from 'react';
import { Drawer, Spin, Result, Typography, Descriptions } from 'antd';
import moment from 'moment-timezone';
import UserAvatar from '../common/UserAvatar';
import CalendarPreview from '../calendar-viewer/CalendarPreview';
import { getUserSettings, getCalendarPreview } from './api';

const { Title, Text } = Typography;

const CalendarDrawer = ({ user, onClose }) => {
    const open = !!user;
    const [loading, setLoading] = useState(false);
    const [settings, setSettings] = useState(null);
    const [schedules, setSchedules] = useState([]);
    const [warning, setWarning] = useState(null);

    useEffect(() => {
        if (!user) {
            setSettings(null);
            setSchedules([]);
            setWarning(null);
            return;
        }

        let cancelled = false;

        const fetchData = async () => {
            setLoading(true);
            setWarning(null);
            setSettings(null);
            setSchedules([]);

            try {
                // Step 1: Get user settings
                const settingsRes = await getUserSettings(user.id);
                const userSettings = settingsRes.data?.data || settingsRes.data;

                if (!userSettings || userSettings === null) {
                    if (!cancelled) setWarning('This user has no calendar settings configured.');
                    return;
                }

                if (!cancelled) setSettings(userSettings);

                // Step 2: Get calendar preview
                const zone = userSettings.zone || moment.tz.guess();
                const from = moment.tz(zone).format('YYYY-MM-DDTHH:mm:ssZ');
                const previewRes = await getCalendarPreview(user.id, from);
                const previewData = previewRes.data?.data || previewRes.data;

                if (previewData?.error === true) {
                    if (!cancelled) setWarning('This user has no connected calendar.');
                    return;
                }

                if (!cancelled) {
                    setSchedules(previewData?.schedules || previewData || []);
                }
            } catch (err) {
                if (!cancelled) setWarning('Failed to load calendar data.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchData();
        return () => { cancelled = true; };
    }, [user]);

    return (
        <Drawer
            title={
                user ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <UserAvatar name={user.name} userId={user.id} size={32} />
                        <span>{user.name} — Calendar</span>
                    </div>
                ) : 'Calendar'
            }
            placement="right"
            width={680}
            open={open}
            onClose={onClose}
            destroyOnClose
        >
            {loading && (
                <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 80 }}>
                    <Spin size="large" />
                </div>
            )}

            {!loading && warning && (
                <Result status="warning" title={warning} />
            )}

            {!loading && !warning && settings && (
                <>
                    <CalendarPreview
                        schedules={schedules}
                        timezone={settings.zone}
                        loading={false}
                        visible={open}
                        availabilitySettings={settings}
                    />

                    <Descriptions
                        column={2}
                        size="small"
                        style={{ marginTop: 16 }}
                        title="Availability Settings"
                    >
                        {settings.zone && (
                            <Descriptions.Item label="Timezone">{settings.zone}</Descriptions.Item>
                        )}
                        {settings.days && (
                            <Descriptions.Item label="Days">{settings.days.join(', ')}</Descriptions.Item>
                        )}
                        {settings.startTime && (
                            <Descriptions.Item label="Start">{settings.startTime}</Descriptions.Item>
                        )}
                        {settings.endTime && (
                            <Descriptions.Item label="End">{settings.endTime}</Descriptions.Item>
                        )}
                        {settings.duration && (
                            <Descriptions.Item label="Duration">{settings.duration} min</Descriptions.Item>
                        )}
                        {settings.gap && (
                            <Descriptions.Item label="Gap">{settings.gap} min</Descriptions.Item>
                        )}
                    </Descriptions>
                </>
            )}
        </Drawer>
    );
};

export default CalendarDrawer;
